const post = {
  slug: "80-20-rule-tipped-employees",
  title: "The 80/20 Rule for Tipped Employees: What Restaurant Owners Need to Know",
  description:
    "The 80/20 rule is one of the top DOL audit triggers in restaurant enforcement. Here's exactly how it works, what counts as side work, and how to avoid costly violations.",
  date: "2026-05-10",
  category: "Tipped Wages",
  readTime: "9 min read",
  content: `# The 80/20 Rule for Tipped Employees: What Restaurant Owners Need to Know

**Last updated: May 2026 | Federal FLSA + State applications**

---

The 80/20 rule is one of the most frequently cited violations in Department of Labor restaurant audit actions — and one of the least understood rules in the entire tip credit framework. Many operators have never heard of it. Many who have heard of it are applying it wrong. And unlike some compliance gray areas, getting this wrong produces a clear, calculable back-wage liability that scales with every employee and every pay period.

This guide covers what the rule actually says, what work counts as side work, how to calculate your exposure, and what documentation protects you in an audit.

**Want to check your current 80/20 exposure?** Enter your non-tipped work percentage in the free [Tipped Employee Wage Compliance Checker](/) and it runs the calculation instantly.

---

## What the 80/20 rule actually says

The 80/20 rule — formally called the "dual jobs" rule under the FLSA — limits how much time a tipped employee can spend on non-tipped tasks while still being paid at the tipped minimum wage.

The rule works like this:

- If a tipped employee spends **20% or less** of their workweek on non-tipped side work, the employer can pay the tipped minimum wage for the entire shift
- If a tipped employee spends **more than 20%** of their workweek on non-tipped side work, the employer must pay the full minimum wage for every hour that exceeds the 20% threshold
- There is also a **30-consecutive-minute rule**: if a tipped employee performs non-tipped work for more than 30 continuous minutes at a stretch — regardless of weekly totals — the employer must pay full minimum wage for that entire continuous block

In plain terms: a server cannot spend more than 8 hours of a 40-hour workweek rolling silverware, stocking, cleaning, or doing prep while being paid $2.13/hr. If they do, you owe them the difference between the tipped wage and the full minimum wage for those excess hours — and that obligation runs retroactively for up to two or three years depending on whether the violation is found to be willful.

---

## Why this rule exists — and why it's back in force

The dual jobs rule has a long regulatory history. The DOL originally established it in the 1960s. For years it was largely unenforced in practice, and many operators treated side work as an informal gray area.

That changed in 2021, when the Biden administration published a final rule reinstating and clarifying the 80/20 standard. After some legal back-and-forth, the rule was upheld and is currently in full force as of 2026. The DOL has explicitly flagged restaurant enforcement — particularly chains and multi-location operators — as a priority area.

If your side work policies haven't been updated since before 2021, assume they need review.

---

## What counts as non-tipped side work?

This is where most operators run into trouble. The distinction isn't "tasks done before or after the dining room opens" — it's about whether the work itself generates tips.

**Non-tipped work (counts toward the 20% limit):**

- Rolling or polishing silverware
- Folding napkins
- Stocking service stations (condiments, sugars, napkins)
- Sweeping or mopping the dining room
- Cleaning and resetting tables (after guests leave, before the restaurant opens)
- Cleaning soda machines, coffee equipment, or service areas
- Prepping garnishes or condiment trays
- Cleaning restrooms
- Taking out trash
- Performing opening or closing checklists unrelated to serving guests

**Tipped work (does not count toward the limit):**

- Greeting and seating guests
- Taking orders and delivering food and drinks
- Running food from the kitchen to tables
- Answering guest questions
- Processing payments
- Refilling drinks during service
- Any task performed in the presence of, and in direct service of, a guest

**The gray area:** Tasks like wiping down tables between guests during a shift, refilling salt and pepper shakers while guests are present, or folding napkins at a side station while monitoring tables are debated. DOL guidance focuses on whether the task is incidental to the tipped service interaction or a distinct non-tipped duty. The safest interpretation: if the task has no guest in front of the employee and isn't directly serving an active table, it likely counts as side work.

---

## The 30-consecutive-minute rule

This is the part of the 80/20 framework that catches operators most off guard.

Even if an employee's total weekly side work is well under 20%, a single block of non-tipped work lasting more than 30 continuous minutes triggers a full minimum wage obligation for that block.

**Example:** A server comes in 45 minutes before their shift to do prep work — cleaning stations, stocking, folding napkins. Even if that 45-minute block is their only side work all week, the employer must pay the full state minimum wage for those 45 minutes, not the tipped cash wage. If the state minimum is $15.00/hr and the tipped wage is $3.63/hr, that's an $11.37 underpayment for those 45 minutes — per employee, per shift this happens.

**Practical implication:** Pre-shift prep assignments, closing duties that run long, and any extended period where an employee is "on the clock but not serving guests" should all be flagged and tracked.

---

## How to calculate your exposure

The dollar exposure from an 80/20 violation is straightforward to calculate.

**Step 1:** Determine the employee's non-tipped hours per week.

> Example: A server works 30 hours/week. They spend 25% of their shift on side work. That's 7.5 hours of non-tipped work per week.

**Step 2:** Calculate the allowed side work hours (20% of total hours).

> 20% × 30 hours = 6 hours allowed. Excess = 7.5 − 6 = 1.5 hours over the limit.

**Step 3:** Calculate the underpayment for the excess hours.

> State minimum wage: $15.00/hr. Tipped cash wage paid: $3.63/hr. Difference: $11.37/hr. Liability: 1.5 hrs × $11.37 = **$17.06/week per employee.**

**Step 4:** Multiply by number of employees and weeks.

> 10 servers × $17.06/week × 52 weeks = **$8,870/year.** Across a two-year back-wage period: **$17,740.**

That's the exposure for a violation that's only 5 percentage points over the limit. Operators with servers doing 30–40% side work — which is common in full-service restaurants — face multiples of this.

Use the [Compliance Checker](/) to run this calculation for your specific pay rates and side work percentages.

---

## The states that go further than federal law

Federal law sets the floor at 20% and 30 consecutive minutes. Several states have stricter rules.

**California:** California does not allow a tip credit at all, so the 80/20 rule doesn't apply in the same way — but California has its own "dual employment" doctrine under IWC wage orders that creates similar obligations. Employers in California cannot use one employee for two distinct roles (tipped and non-tipped) without paying the appropriate wage for each.

**New York:** New York's Department of Labor has issued guidance interpreting the 80/20 rule more strictly than the federal standard in some enforcement contexts. New York operators should review the current NYS DOL tip credit guidance, not just the federal rule.

**Washington D.C.:** D.C. eliminated the tip credit entirely via Initiative 82. The 80/20 rule is moot for D.C. employers — all employees receive the full minimum wage regardless of tips. But operators who previously structured side work policies around the tip credit need to revisit their overall wage structure.

**Chicago:** Chicago's tip credit is phasing out by July 1, 2028. As the tip credit shrinks toward elimination, the 80/20 rule becomes less consequential for Chicago operators year over year.

---

## What a DOL audit actually looks for

When the DOL audits a restaurant for 80/20 compliance, investigators typically ask for:

- Time records showing when tipped employees clocked in and out
- Any task assignment sheets, side work checklists, or opening/closing duty rosters
- Manager testimony about what side work is assigned and for how long
- Employee interviews about their typical shift structure
- Point-of-sale data showing when servers first entered orders (as a proxy for when they were actually serving guests)

The absence of task-level time records is itself a red flag. If you cannot demonstrate what percentage of each shift was spent on tipped vs. non-tipped work, the DOL investigator may estimate — and estimates rarely favor the employer.

---

## How to protect yourself

### 1. Track side work time separately

The most important thing you can do is create a record. This doesn't have to be complex — a simple daily side work log that each server initials, showing what task they did and how long it took, creates a defensible paper trail. Some POS systems can help with this if configured correctly.

### 2. Cap side work assignments at 15%

Build a 5-point buffer into your scheduling. If the legal limit is 20%, design your side work assignments so that no server regularly exceeds 15% of their shift on non-tipped tasks. This gives you margin before a violation triggers.

### 3. Separate pre-shift and post-shift duties from the tipped shift

If servers need to come in early for substantial prep work, consider clocking them in at full minimum wage for that pre-shift period and switching them to tipped wage once service begins. This eliminates the 30-consecutive-minute exposure entirely for that block.

### 4. Review your opening and closing checklists

These are the most common source of 80/20 violations. Map out every task on your opening and closing lists, estimate the time each takes, and calculate whether the total exceeds 20% of a typical shift for employees who complete them.

### 5. Train managers on the rule

Most 80/20 violations aren't intentional — they happen because managers assign side work without any awareness that a federal time limit applies. A 30-minute manager training that explains the rule and introduces a tracking system is a low-cost protection against a high-cost audit result.

---

## Frequently asked questions

**Does the 20% limit apply per shift or per workweek?**
Per workweek under federal law. You average the non-tipped time across the full 7-day workweek. However, the 30-consecutive-minute rule applies per incident — any single continuous block of non-tipped work over 30 minutes triggers the obligation regardless of weekly totals.

**What if a server voluntarily stays late to do side work?**
Doesn't matter. The FLSA's obligations apply regardless of whether the employee volunteered or was assigned the work. If they're on the clock doing non-tipped tasks, the time counts.

**Can I pay a blended rate to cover side work?**
No. You cannot average the tipped and non-tipped wage rates together. The FLSA requires the full minimum wage for excess non-tipped hours — paying a blended rate that falls below the minimum for those hours is itself a violation.

**Does the rule apply to bartenders?**
Yes, if the employer is claiming a tip credit for bartenders. Bartenders who spend significant time on non-tipped tasks — cleaning, stocking, cutting fruit, cleaning the bar area — are subject to the same 20%/30-minute limits as servers.

**What is the lookback period for back wages?**
Two years for non-willful violations, three years for willful violations under the FLSA. State law may extend this further — California, for example, has a three-year statute of limitations for wage claims, and some claims can be brought under the four-year UCL.

---

## Bottom line

The 80/20 rule isn't theoretical exposure — it's a live audit trigger with a straightforward dollar calculation attached to it. Most full-service restaurant operators are somewhere on the spectrum from technical compliance to significant violation, and few have the documentation to defend themselves if audited.

The practical steps are not expensive: build a side work log, cap assignments at 15%, and train your managers. Those three actions reduce your exposure materially.

If you want to see your specific numbers — your state's minimum wage, your current non-tipped percentage, and your estimated weekly and annual dollar exposure — run it through the free [Tipped Employee Wage Compliance Checker at WorkplacePrompts.ai](/). It takes under 60 seconds.

---

*This post is for informational purposes only and does not constitute legal advice. Wage laws change frequently. Consult a licensed employment attorney before making payroll changes.*

*Some links in this post are affiliate links. We may earn a commission if you sign up for a service through our link, at no additional cost to you.*`,
};

export default post;
